import { useEffect, type ReactNode } from "react";
import styles from "./AlertDialog.module.css";
import { cx } from "../cx";
import { ActionButton } from "../components/ActionButton";

export type AlertDialogProps = {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  tone?: "neutral" | "brand" | "critical";
  /** pair = 취소+확인 (기본) · single = 확인만 · stacked = 주요 + 보조(ghost) 세로 */
  action?: "pair" | "single" | "stacked";
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimary: () => void;
  onSecondary?: () => void;
  /** 오버레이·Esc. 기본은 onSecondary ?? onPrimary */
  onDismiss?: () => void;
  loading?: boolean;
  className?: string;
};

/** SEED AlertDialog (Figma 79:1956). max 272px. */
export function AlertDialog({
  open,
  title,
  description,
  tone = "neutral",
  action = "pair",
  primaryLabel = "확인",
  secondaryLabel = "취소",
  onPrimary,
  onSecondary,
  onDismiss,
  loading = false,
  className,
}: AlertDialogProps) {
  const dismiss = onDismiss ?? onSecondary ?? onPrimary;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && dismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);
  if (!open) return null;

  const primaryVariant = tone === "neutral" ? "neutralSolid" : "brandSolid";
  const primary = (
    <ActionButton
      size="medium"
      block
      variant={primaryVariant}
      className={cx(tone === "critical" && styles.criticalPrimary)}
      onClick={onPrimary}
      loading={loading}
    >
      {primaryLabel}
    </ActionButton>
  );

  return (
    <div className={styles.overlay} onClick={dismiss}>
      <div role="alertdialog" aria-modal="true" className={cx(styles.dialog, className)} onClick={(e) => e.stopPropagation()}>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <div className={cx(styles.actions, styles[action])}>
          {action === "pair" && (
            <>
              <ActionButton size="medium" block variant="neutralWeak" onClick={onSecondary} disabled={loading}>
                {secondaryLabel}
              </ActionButton>
              {primary}
            </>
          )}
          {action === "single" && primary}
          {action === "stacked" && (
            <>
              {primary}
              <ActionButton size="small" block variant="ghost" className={styles.stackedSecondary} onClick={onSecondary} disabled={loading}>
                {secondaryLabel}
              </ActionButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
